import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Sparkles,
  CheckSquare,
  Square,
  Save,
  RefreshCw,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";

const ALL_KINDS = [
  "mcq_single",
  "mcq_multi",
  "mcq_matrix",
  "true_false",
  "matching",
  "drag_to_group",
  "image_matching",
  "ordering",
  "ordering_weighted",
  "cloze_text",
  "cloze_regex",
  "word_bank",
  "short_text",
  "essay",
  "numeric",
  "drag_drop_item_target",
  "sentence_builder",
  "select_sentence",
  "passage_mcq",
  "audio_prompt",
  "audio_gate",
  "speaking_integrated",
  "writing_prompt",
  "file_upload",
  "c_test",
  "sentence_reconstruction",
];

const KIND_LABELS = {
  mcq_single: "چهار گزینه‌ای (تکی)",
  mcq_multi: "چند گزینه‌ای",
  mcq_matrix: "ماتریسی",
  true_false: "درست / غلط",
  matching: "وصل کردنی",
  drag_to_group: "کشیدن به گروه",
  image_matching: "تطبیق تصویر",
  ordering: "ترتیب",
  ordering_weighted: "ترتیب وزن‌دار",
  cloze_text: "جای خالی",
  cloze_regex: "جای خالی (Regex)",
  word_bank: "Word Bank",
  short_text: "پاسخ کوتاه",
  essay: "انشا",
  numeric: "عددی",
  drag_drop_item_target: "Drag Drop Target",
  sentence_builder: "Sentence Builder",
  select_sentence: "انتخاب جمله",
  passage_mcq: "درک مطلب",
  audio_prompt: "پرسش صوتی",
  audio_gate: "Audio Gate",
  speaking_integrated: "Speaking Integrated",
  writing_prompt: "Writing Prompt",
  file_upload: "آپلود فایل",
  c_test: "C-Test",
  sentence_reconstruction: "بازسازی جمله",
};

function getPreviewTitle(item) {
  // تلاش برای پیدا کردن stem
  const stem =
    item?.stem?.fa ||
    item?.stem?.en ||
    item?.body?.text?.fa ||
    item?.body?.text?.en ||
    item?.body?.prompt?.fa ||
    item?.body?.prompt?.en ||
    "";

  const s = String(stem)
    .replace(/<[^>]*>/g, "")
    .trim();
  if (!s) return "بدون عنوان";
  return s.length > 80 ? s.slice(0, 80) + "..." : s;
}

export default function AiItemGeneratorModal({
  isOpen,
  onClose,
  token,
  categoryId,
  defaultLanguage = "auto",
  onGenerated, // (savedItems) => void
  examId,
}) {
  const { i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  // form state
  const [count, setCount] = useState(10);
  const [difficulty, setDifficulty] = useState("medium");
  const [language, setLanguage] = useState(defaultLanguage || "auto");
  const [seedText, setSeedText] = useState("");
  const [allowedKinds, setAllowedKinds] = useState(
    () => new Set([ALL_KINDS[0]])
  );

  // generation state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // raw generated items (not saved)
  const [generated, setGenerated] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(() => new Set());

  useEffect(() => {
    if (!isOpen) return;
    setCount(10);
    setDifficulty("medium");
    setLanguage(defaultLanguage || "auto");
    setSeedText("");
    setAllowedKinds(new Set([ALL_KINDS[0]]));
    setGenerated([]);
    setSelectedIdx(new Set());
    setLoading(false);
    setSaving(false);
  }, [isOpen, defaultLanguage]);

  const selectedKindsArr = useMemo(
    () => Array.from(allowedKinds),
    [allowedKinds]
  );

  const toggleKind = (k) => {
    setAllowedKinds((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const canGenerate =
    !loading &&
    !!categoryId &&
    Number(count) > 0 &&
    selectedKindsArr.length > 0;

  const handleGenerate = async () => {
    if (!canGenerate) return;

    setLoading(true);
    try {
      const res = await httpRequest(
        "POST",
        "/ai/items/generate",
        {
          categoryId,
          examId,
          count: Number(count),
          difficulty,
          language,
          allowedKinds: selectedKindsArr,
          seedText: seedText?.trim() || undefined,
        },
        { "x-access-token": token }
      );

      const data = res?.data?.data || res?.data;
      const rawItems = data?.items || data || [];

      if (!Array.isArray(rawItems) || rawItems.length === 0) {
        toast.warn("چیزی تولید نشد");
        setGenerated([]);
        setSelectedIdx(new Set());
        return;
      }

      setGenerated(rawItems);
      // پیش‌فرض: همه انتخاب بشن (می‌تونی تغییر بدی)
      setSelectedIdx(new Set(rawItems.map((_, i) => i)));

      toast.success(`${rawItems.length} سؤال تولید شد ✅ (هنوز ذخیره نشده)`);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "خطا در تولید سوال"
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectItem = (index) => {
    setSelectedIdx((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const selectAll = () => setSelectedIdx(new Set(generated.map((_, i) => i)));
  const clearAll = () => setSelectedIdx(new Set());

  const selectedItems = useMemo(() => {
    return generated.filter((_, i) => selectedIdx.has(i));
  }, [generated, selectedIdx]);

  const canSave = !saving && generated.length > 0 && selectedItems.length > 0;

  const handleSaveSelected = async () => {
    if (!canSave) {
      toast.error("حداقل یک سؤال را انتخاب کن");
      return;
    }

    setSaving(true);
    try {
      // ✅ فقط آیتم‌های انتخاب‌شده ذخیره می‌شوند
      const saveRes = await httpRequest(
        "POST",
        "/ai/items/save",
        { items: selectedItems },
        { "x-access-token": token }
      );

      const saveData = saveRes?.data?.data || saveRes?.data;
      const savedItems = saveData?.items || [];

      if (!Array.isArray(savedItems) || savedItems.length === 0) {
        toast.error("ذخیره انجام نشد یا خروجی خالی بود");
        return;
      }

      toast.success(`${savedItems.length} سؤال ذخیره شد ✅`);
      onGenerated?.(savedItems);
      onClose?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "خطا در ذخیره سوال‌ها"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl p-5 max-h-[80vh] overflow-scroll"
        dir={isRtl ? "rtl" : "ltr"}
      >
        {/* header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold">تولید سوال با هوش مصنوعی</h2>
          </div>
          <button
            className="p-2 rounded-lg hover:bg-gray-100"
            onClick={onClose}
            disabled={loading || saving}
            aria-label="close"
          >
            <X />
          </button>
        </div>

        {/* ==========================
            1) FORM (Generate options)
           ========================== */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                تعداد سوال
              </label>
              <input
                type="number"
                min={1}
                max={50}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">سختی</label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
              >
                <option value="easy">آسان</option>
                <option value="medium">متوسط</option>
                <option value="hard">سخت</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">زبان</label>
              <select
                className="w-full border rounded-lg px-3 py-2"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="auto">Auto</option>
                <option value="fa">فارسی</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>

          {/* kinds */}
          <div>
            <label className="block text-sm font-medium mb-2">
              نوع سوال‌های مجاز
            </label>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-[180px] overflow-auto border rounded-lg p-2">
              {ALL_KINDS.map((k) => {
                const checked = allowedKinds.has(k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleKind(k)}
                    className={`px-3 py-2 rounded-lg border text-sm text-right ${
                      checked
                        ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="font-medium">{KIND_LABELS[k] || k}</div>
                    <div className="text-xs opacity-70">{k}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* seed */}
          <div>
            <label className="block text-sm font-medium mb-1">
              متن راهنما (اختیاری)
            </label>
            <textarea
              value={seedText}
              onChange={(e) => setSeedText(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 min-h-[90px]"
              placeholder="مثلاً موضوعات مورد نظر، یا چند نمونه سوال، یا نکات مهم…"
            />
          </div>

          {/* generate btn */}
          <div className="flex justify-end gap-2">
            <button
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              {loading ? "در حال تولید..." : "تولید سوال"}
            </button>
          </div>

          {/* ==========================
              2) GENERATED ITEMS PREVIEW
             ========================== */}
          {generated.length > 0 && (
            <div className="mt-6 border rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                <div className="text-sm font-semibold text-gray-800">
                  سوال‌های تولید شده ({generated.length})
                </div>

                <div className="flex items-center gap-2 text-xs">
                  <button
                    onClick={selectAll}
                    className="px-2 py-1 rounded-md border hover:bg-white"
                  >
                    انتخاب همه
                  </button>
                  <button
                    onClick={clearAll}
                    className="px-2 py-1 rounded-md border hover:bg-white"
                  >
                    پاک کردن
                  </button>
                </div>
              </div>

              <div className="max-h-[260px] overflow-auto divide-y">
                {generated.map((it, idx) => {
                  const checked = selectedIdx.has(idx);
                  return (
                    <div
                      key={idx}
                      className="p-3 flex items-start justify-between gap-3"
                    >
                      <div className="flex gap-3 flex-1">
                        <button
                          type="button"
                          onClick={() => toggleSelectItem(idx)}
                          className="mt-1 p-1 rounded hover:bg-gray-100"
                        >
                          {checked ? (
                            <CheckSquare className="w-5 h-5 text-indigo-600" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400" />
                          )}
                        </button>

                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {getPreviewTitle(it)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {KIND_LABELS[it?.kind] || it?.kind || "unknown"} •{" "}
                            {it?.points ?? 1} امتیاز
                          </div>
                        </div>
                      </div>

                      <div className="text-[11px] text-gray-400 whitespace-nowrap">
                        #{idx + 1}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  انتخاب شده:{" "}
                  <span className="font-semibold text-indigo-700">
                    {selectedItems.length}
                  </span>
                </div>

                <button
                  onClick={handleSaveSelected}
                  disabled={!canSave}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? "در حال ذخیره..." : "ذخیره و افزودن به لیست سوالات"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
